import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Flex,
  Divider,
  Badge,
  Skeleton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  VStack,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { FiArrowLeft, FiInfo } from 'react-icons/fi';
import { ProjectService } from '../../services/projectService';
import { Project } from '../../types/project';
import { Task } from '../../types/task';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

/**
 * 分享项目详情页面 - V0版本
 * 该页面不需要认证，任何人都可以访问
 */
export const SharedProjectDetail = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['v0/projects', 'v0/common', 'common']);

  // 状态
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 颜色
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  // 加载分享项目数据
  useEffect(() => {
    const fetchSharedProject = async () => {
      if (!shareId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await ProjectService.getSharedProject(shareId);
        setProject(response.project);
        setTasks(response.tasks || []);
      } catch (err) {
        console.error('Error fetching shared project:', err);
        setError(t('v0/projects:sharedProject.loadError'));
      } finally {
        setLoading(false);
      }
    };

    fetchSharedProject();
  }, [shareId]);

  // 格式化日期
  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: zhCN });
    } catch (error) {
      return t('common:timeAgo.unknown');
    }
  };

  // 返回首页
  const handleBackToHome = () => {
    navigate('/');
  };

  // 登录
  const handleLogin = () => {
    navigate('/login');
  };

  // 加载中状态
  if (loading) {
    return (
      <Container maxW="4xl" py={8}>
        <Flex mb={6} align="center">
          <Button
            leftIcon={<FiArrowLeft />}
            variant="ghost"
            onClick={handleBackToHome}
            mr={4}
          >
            {t('v0/common:actions.backToHome')}
          </Button>
          <Skeleton height="30px" width="200px" />
        </Flex>
        <Skeleton height="100px" mb={6} />
        <Skeleton height="20px" mb={2} />
        <Skeleton height="20px" mb={6} />
        <Divider mb={6} />
        <Heading size="md" mb={4}>
          <Skeleton height="24px" width="150px" />
        </Heading>
        <VStack spacing={4} align="stretch">
          <Skeleton height="60px" />
          <Skeleton height="60px" />
          <Skeleton height="60px" />
        </VStack>
      </Container>
    );
  }

  // 错误状态
  if (error) {
    return (
      <Container maxW="4xl" py={8}>
        <Flex mb={6} align="center">
          <Button
            leftIcon={<FiArrowLeft />}
            variant="ghost"
            onClick={handleBackToHome}
            mr={4}
          >
            {t('v0/common:actions.backToHome')}
          </Button>
          <Heading size="lg">{t('v0/projects:sharedProject.title')}</Heading>
        </Flex>
        <Alert
          status="error"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          height="200px"
          borderRadius="md"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            {t('v0/projects:sharedProject.cannotLoad')}
          </AlertTitle>
          <AlertDescription maxWidth="sm">
            {error}
          </AlertDescription>
          <Button mt={4} colorScheme="blue" onClick={handleLogin}>
            {t('v0/projects:sharedProject.loginToViewMore')}
          </Button>
        </Alert>
      </Container>
    );
  }

  // 项目不存在
  if (!project) {
    return (
      <Container maxW="4xl" py={8}>
        <Flex mb={6} align="center">
          <Button
            leftIcon={<FiArrowLeft />}
            variant="ghost"
            onClick={handleBackToHome}
            mr={4}
          >
            {t('v0/common:actions.backToHome')}
          </Button>
          <Heading size="lg">{t('v0/projects:sharedProject.title')}</Heading>
        </Flex>
        <Alert
          status="warning"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          height="200px"
          borderRadius="md"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            {t('v0/projects:sharedProject.projectNotExist')}
          </AlertTitle>
          <AlertDescription maxWidth="sm">
            {t('v0/projects:sharedProject.projectNotExistDescription')}
          </AlertDescription>
          <Button mt={4} colorScheme="blue" onClick={handleLogin}>
            {t('v0/projects:sharedProject.loginToViewMore')}
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="4xl" py={8}>
      {/* 头部 */}
      <Flex mb={6} align="center">
        <Button
          leftIcon={<FiArrowLeft />}
          variant="ghost"
          onClick={handleBackToHome}
          mr={4}
        >
          {t('v0/common:actions.backToHome')}
        </Button>
        <Heading size="lg">{t('v0/projects:sharedProject.title')}</Heading>
      </Flex>

      {/* 项目信息 */}
      <Box
        bg={bgColor}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={borderColor}
        p={6}
        mb={6}
        shadow="sm"
      >
        <Heading size="xl" mb={3} color={textColor}>
          {project.title}
        </Heading>

        <Text color={mutedColor} mb={4}>
          {t('v0/projects:sharedProject.updatedAt', { time: formatDate(project.updatedAt) })}
        </Text>

        {project.context && (
          <Text color={textColor} whiteSpace="pre-wrap">
            {project.context}
          </Text>
        )}

        <Flex mt={6} justify="space-between" align="center">
          <Badge colorScheme="green" px={2} py={1} borderRadius="md">
            {t('v0/projects:sharedProject.publiclyShared')}
          </Badge>

          <Button colorScheme="blue" onClick={handleLogin}>
            {t('v0/projects:sharedProject.loginToViewMore')}
          </Button>
        </Flex>
      </Box>

      {/* 任务列表 */}
      <Divider mb={6} />
      <Heading size="md" mb={4}>
        {t('v0/projects:sharedProject.projectTasks', { count: tasks.length })}
      </Heading>

      {tasks.length === 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          {t('v0/projects:sharedProject.noTasks')}
        </Alert>
      ) : (
        <VStack spacing={4} align="stretch">
          {tasks.map((task) => (
            <Box
              key={task.id}
              p={4}
              borderWidth="1px"
              borderRadius="md"
              borderColor={borderColor}
              bg={bgColor}
            >
              <Flex align="center" mb={2}>
                <Text
                  fontWeight="medium"
                  textDecoration={task.done ? 'line-through' : 'none'}
                  color={task.done ? mutedColor : textColor}
                >
                  {task.title}
                </Text>
                <HStack spacing={2} ml="auto">
                  {task.vital && (
                    <Badge colorScheme="purple">{t('v0/common:common.important')}</Badge>
                  )}
                  {task.urgent && (
                    <Badge colorScheme="red">{t('v0/common:common.urgent')}</Badge>
                  )}
                </HStack>
              </Flex>

              {task.notes && (
                <Text
                  fontSize="sm"
                  color={mutedColor}
                  noOfLines={2}
                  textDecoration={task.done ? 'line-through' : 'none'}
                >
                  {task.notes}
                </Text>
              )}
            </Box>
          ))}
        </VStack>
      )}

      {/* 底部提示 */}
      <Alert status="info" mt={8} borderRadius="md">
        <AlertIcon />
        <Box>
          <AlertTitle>{t('v0/projects:sharedProject.readOnlyMode')}</AlertTitle>
          <AlertDescription>
            {t('v0/projects:sharedProject.readOnlyDescription')}
          </AlertDescription>
        </Box>
      </Alert>
    </Container>
  );
};
